import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Loader2, Pencil, RefreshCw, Send, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { PosAdjustmentForm } from "@/components/pos-documents/PosAdjustmentForm";
import { translatePosError } from "@/components/pos-documents/error-translations";
import { POS_STATUS_BADGE_VARIANT } from "@/components/pos-documents/format";
import { RequireAuth } from "@/components/RequireAuth";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDiscardPosAdjustment,
  useDownloadPosAdjustmentArtifact,
  useEmitPosAdjustment,
  usePosAdjustment,
  usePosAdjustmentArtifacts,
  useRefreshPosAdjustmentStatus,
} from "@/lib/api/pos-document-adjustments.queries";
import { useActiveCompany } from "@/lib/company-context";
import { useTeamPermission } from "@/lib/team-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";

export const Route = createFileRoute("/companies/$companyId/pos/ajustes/$adjustmentId")({
  component: PosAdjustmentDetailPage,
});

function PosAdjustmentDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PosAdjustmentDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function PosAdjustmentDetailContent() {
  const formatPosMoney = useMoneyFormatter();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId, adjustmentId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const query = usePosAdjustment(companyId, adjustmentId);
  const emit = useEmitPosAdjustment(companyId);
  const discard = useDiscardPosAdjustment(companyId);
  const canWrite = useTeamPermission("pos.write");
  const refresh = useRefreshPosAdjustmentStatus(companyId);
  const artifacts = usePosAdjustmentArtifacts(companyId, adjustmentId, !!query.data?.processId);
  const download = useDownloadPosAdjustmentArtifact(companyId, adjustmentId);
  const readiness = useCompanyDianReadiness(companyId, "pos_ajuste");
  const readinessReason = useDianReadinessReason(readiness, t);

  useEffect(() => {
    setActiveCompanyId(companyId);
  }, [companyId, setActiveCompanyId]);

  if (query.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title={t("pos.ajuste.detail.error_title")}
        description={t("pos.ajuste.detail.error_body")}
      />
    );
  }

  const document = query.data;
  const canEmit = document.status === "draft" || document.status === "failed";
  // A draft, or an adjustment DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && document.editable;
  const canDiscard = canWrite && document.status === "draft" && document.documentNumber === 0;

  async function onDiscard() {
    try {
      await discard.mutateAsync({ adjustmentId: document.id, expectedRevision: document.revision });
      toast.success(t("pos.ajuste.detail.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/pos/ajustes", params: { companyId } });
    } catch (error) {
      toast.error(translatePosError(error, t));
      setDiscardOpen(false);
    }
  }

  async function onEmit() {
    try {
      await emit.mutateAsync(document.id);
      toast.success(t("pos.ajuste.detail.emit_queued"));
      setEditing(false);
    } catch (error) {
      showEmitErrorToast(error, translatePosError(error, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    }
  }

  async function onRefresh() {
    try {
      await refresh.mutateAsync(document.id);
      toast.success(t("pos.ajuste.detail.refresh_done"));
    } catch (error) {
      toast.error(translatePosError(error, t));
    }
  }

  async function onCopyCude() {
    if (!document.cude) return;
    await navigator.clipboard.writeText(document.cude);
    toast.success(t("pos.ajuste.detail.cude_copied"));
  }

  async function onDownload(name: string) {
    try {
      const blob = await download.mutateAsync({ name });
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = `${document.documentId}-${name}`;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(translatePosError(error, t));
    }
  }

  return (
    <div className="space-y-6">
      <DianReadinessBanner companyId={companyId} documentKind="pos_ajuste" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/companies/$companyId/pos/ajustes"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t("pos.ajuste.detail.back")}
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{document.documentId}</h1>
            <Badge variant={POS_STATUS_BADGE_VARIANT[document.status]}>
              {t(`pos.status.${document.status}`)}
            </Badge>
            <Badge variant="secondary">{t(`pos.ajuste.note_type.${document.noteType}`)} · {document.adjustmentTypeCode}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {document.originalDocumentId} · {formatPosMoney(document.payableAmount)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("pos.detail.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("pos.ajuste.detail.discard")}
            </Button>
          ) : null}
          {document.processId ? (
            <Button variant="outline" disabled={refresh.isPending} onClick={() => void onRefresh()}>
              <RefreshCw className={refresh.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              {t("pos.detail.refresh_status")}
            </Button>
          ) : null}
          {canEmit && !editing ? (
            <Button
              disabled={emit.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
              onClick={() => void onEmit()}
            >
              <Send className="h-4 w-4" />
              {t("pos.detail.emit_now")}
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <PosAdjustmentForm
          companyId={companyId}
          initialAdjustment={document}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("pos.ajuste.detail.reference.title")}</CardTitle>
              <CardDescription>{t("pos.ajuste.detail.reference.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">{t("pos.ajuste.table.original")}:</span>{" "}
                <Link
                  to="/companies/$companyId/pos/$documentId"
                  params={{ companyId, documentId: document.originalPosDocumentId }}
                  className="underline"
                >
                  {document.originalDocumentId}
                </Link>
              </p>
              <p><span className="text-muted-foreground">{t("pos.ajuste.form.discrepancy")}:</span> {document.discrepancyResponseCode} · {t(`pos.ajuste.discrepancy.${document.discrepancyResponseCode}`)}</p>
              <p>{document.discrepancyDescription}</p>
              {document.cude ? (
                <Button variant="outline" size="sm" onClick={() => void onCopyCude()}>
                  <Copy className="h-3.5 w-3.5" />
                  {t("pos.ajuste.detail.copy_cude")}
                </Button>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("pos.ajuste.form.lines.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pos.form.line_description")}</TableHead>
                    <TableHead>{t("pos.form.quantity")}</TableHead>
                    <TableHead>{t("pos.form.unit_price")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {document.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell>{line.quantity}</TableCell>
                      <TableCell>{formatPosMoney(line.unitPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="mt-3 text-sm font-medium">{t("pos.totals.payable")}: {formatPosMoney(document.payableAmount)}</p>
            </CardContent>
          </Card>
          {document.processId ? (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">{t("pos.artifacts.title")}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {(artifacts.data?.artifacts ?? []).map((artifact) => (
                  <Button key={artifact.name} variant="outline" size="sm" onClick={() => void onDownload(artifact.name)}>
                    {artifact.name}
                  </Button>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("pos.ajuste.detail.discard_title", { documentId: document.documentId })}
        description={t("pos.ajuste.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}
